import React from 'react'

export default sliceMapping => ({
  body,
  wrapper = r => r, ...rest
}) => {
  // Try and make it show we only show the error message when previewing
  // Only throw errors when not in production
  // In prod, not previewing, render nothing
  const hasWindow = typeof window !== 'undefined'
  const isProd = process.env.NODE_ENV === 'production'
  const showErrors = isProd && hasWindow && window.__PRISMIC_PREVIEW_DATA__
  const throwErrors = !isProd && hasWindow

  return body.map(({ __typename: type, ...data }, i) => {
    const sliceMapper = sliceMapping[type]

    return (
      <Slice
        key={i}
        index={i}
        showErrors={showErrors}
        throwErrors={throwErrors}
        sliceMapper={sliceMapper}
        passThru={rest}
        data={data}
        type={type}
        wrapper={wrapper}
      />
    )
  })
}

const Slice = ({
  showErrors,
  throwErrors,
  sliceMapper,
  passThru,
  type,
  data,
  wrapper,
  index
}) => {
  if (!sliceMapper || !sliceMapper.component || !sliceMapper.dataToProps) {
    const errorMsg = `Unknown slice type or component for ${type}`

    if (!showErrors) {
      if (throwErrors) {
        throw new Error(errorMsg)
      } else {
        console.error(errorMsg)
        return null
      }
    }

    return (
      <DisplayMessage type={type} wrapper={wrapper}>
        Something went wrong finding the corresponding UI component for this
        slice type '{type}'
      </DisplayMessage>
    )
  }

  const { component: Cmp, dataToProps } = sliceMapper

  let dataProps

  try {
    dataProps = dataToProps(data, passThru)
  } catch (e) {
    if (!showErrors) {
      if (throwErrors) {
        throw e
      } else {
        console.error(`Error for slice type '${type}'`, e)
        return null
      }
    }

    return (
      <DisplayMessage type={type} wrapper={wrapper}>
        <p>Something went wrong trying to render this UI component (slice).</p>

        <p>
          These types of errors are usually caused by fields in the relevant
          entry being missing.
        </p>

        <p>slice type '{type}'</p>
      </DisplayMessage>
    )
  }

  const props = {
    ...passThru,
    ...dataProps
  }

  return (
    <ErrorBoundary
      showErrors={showErrors}
      throwErrors={throwErrors}
      type={type}
      data={dataProps}
    >
      {wrapper(<Cmp {...props} />, index)}
    </ErrorBoundary>
  )
}

class ErrorBoundary extends React.Component {
  constructor (props) {
    super(props)

    this.state = { error: null }
  }

  static getDerivedStateFromError (error) {
    // Update state so the next render will show the fallback UI.
    return { error }
  }

  render () {
    const {
      children,
      type,
      data,
      showErrors,
      throwErrors
    } = this.props

    if (this.state.error) {
      if (!showErrors) {
        if (throwErrors) {
          throw this.state.error
        } else {
          console.error(this.state.error)
          return null
        }
      }

      return <DisplayMessage data={data} type={type} />
    }

    return children
  }
}

const DisplayMessage = ({ type, data, children, wrapper = e => e }) => {
  return wrapper(
    <div
      style={{
        background: 'white',
        padding: '40px',
        fontWeight: 'normal'
      }}
    >
      <h5 data-type={type}>Error, something went wrong rendering this component!</h5>

      {typeof children === 'string' ? <p>{children}</p> : children}

      {data && (
        <div style={{ display: 'none' }}>
          <pre>{JSON.stringify(data)}</pre>
        </div>
      )}
    </div>
  )
}
